import { isPvpOnlyJewel } from './jewels';
import { type ModeNotice, PVP_ONLY_BADGE, pvpOnlyNotice } from './mode-notice';

/**
 * **Onde uma joia NÃO produz efeito**, perguntado ao catálogo.
 *
 * Separado de `jewel-effect-text` no gate V28G.W.1, quando aquele se mudou
 * para `game-domain`. Este ficou: a pergunta que ele responde - "esta joia vale
 * na caça?" - lê-se inteiramente de `supportedModes`, e não precisa de saber
 * uma única constante de mecânica.
 *
 * A ETIQUETA e a FORMA do aviso mudaram-se para `mode-notice.ts` no gate V28H.B,
 * quando as cartas passaram a precisar das mesmas. Os dois nomes daqui
 * continuam a existir e a valer exatamente o que valiam: a sigla é a mesma
 * string, e a frase é a que nomeia uma joia.
 */

/**
 * **A etiqueta de uma joia que só vale no duelo.** Aprovada, e escrita uma vez.
 *
 * Curta de propósito: é uma ETIQUETA, e vai ao lado do nome numa carta que já
 * tem nome, nível, efeito e estado. Uma frase inteira ali competiria com todos
 * os quatro.
 */
export const JEWEL_PVP_ONLY_BADGE = PVP_ONLY_BADGE;

/**
 * **A explicação, por extenso.** Aprovada, e escrita uma vez.
 *
 * ## O que ela diz, e o que ela deliberadamente NÃO diz
 *
 * Diz que a joia não produz efeito na Caça. Não diz que ela é inválida, que não
 * pode ser encaixada, que está desligada, ou que um dia funcionará - porque
 * nenhuma dessas quatro é verdade. Uma joia exclusivamente de duelo continua a
 * poder ocupar um socket e a viajar no perfil congelado de uma caçada; o que ela
 * não faz é produzir efeito lá.
 *
 * ## Porque as duas metades são constantes separadas
 *
 * Porque servem posições diferentes na mesma carta - a etiqueta ao lado do nome,
 * a frase por baixo do efeito - e porque a tecnologia assistiva lê a frase e não
 * a sigla. Uma string única obrigaria a cortá-la em dois na apresentação, que é
 * onde um corte deixa de ser revisto.
 */
export const JEWEL_PVP_ONLY_NOTE = 'Esta joia não produz efeito na Caça.';

/**
 * O aviso de modo desta joia, ou `null` quando não há aviso a dar.
 *
 * ## Porque isto é uma função e não um `if` na interface
 *
 * Porque a condição não é "é a Reflexão". É "produz efeito no duelo e não na
 * caça" - {@link isPvpOnlyJewel} - e essa pergunta é feita ao CATÁLOGO. Uma
 * interface que comparasse ids teria uma segunda lista a envelhecer ao lado da
 * primeira, e a segunda joia exclusiva de duelo entraria em produção sem aviso.
 *
 * `null` para as que valem na caça, e `null` também para as cinco que não valem
 * em modo nenhum: anunciar exclusividade de duelo a uma peça que também não faz
 * nada no duelo seria prometer um efeito que ela não tem.
 */
export function jewelModeNotice(definitionId: string): ModeNotice | null {
  if (!isPvpOnlyJewel(definitionId)) {
    return null;
  }
  return pvpOnlyNotice(JEWEL_PVP_ONLY_NOTE);
}
